/**
 * Poster Video — Frontend interaction (view.js)
 *
 * Vanilla JS, no external dependencies. No jQuery.
 * Enqueued automatically by WordPress when the block is rendered
 * (declared as "viewScript" in block.json).
 *
 * Strategy: event delegation on document so the script works correctly
 * inside Query Loop blocks and any dynamically injected content.
 *
 * Accessibility:
 *  - Play trigger is a <button> (keyboard-activatable natively).
 *  - aria-label on the button describes the action.
 *  - iframe receives a descriptive title attribute.
 *
 * @package waiis
 */

'use strict';

/**
 * Replace the poster/button content with a responsive iframe (YouTube / Vimeo).
 *
 * @param {HTMLElement} block - The .wp-block-custom-poster-video wrapper.
 */
function loadIframe( block ) {
	var embedUrl = block.getAttribute( 'data-embed-url' );
	if ( ! embedUrl ) {
		return;
	}

	var provider = block.getAttribute( 'data-provider' ) || 'video';

	// Build the iframe.
	var iframe = document.createElement( 'iframe' );
	iframe.src                = embedUrl;
	iframe.className          = 'poster-video__iframe';
	iframe.title              = 'youtube' === provider
		? 'YouTube video player'
		: 'Vimeo video player';
	iframe.allow              = 'autoplay; fullscreen; picture-in-picture';
	iframe.allowFullscreen    = true;
	iframe.setAttribute( 'frameborder', '0' );

	// Swap the inner content.
	var inner = block.querySelector( '.poster-video__inner' );
	if ( inner ) {
		inner.innerHTML = '';
		inner.appendChild( iframe );
	} else {
		block.innerHTML = '';
		block.appendChild( iframe );
	}

	block.classList.add( 'is-playing' );
	iframe.focus();
}

/**
 * Replace the poster/button content with a native <video> element (MP4).
 *
 * @param {HTMLElement} block - The .wp-block-custom-poster-video wrapper.
 */
function loadMp4Video( block ) {
	var videoUrl = block.getAttribute( 'data-mp4-url' );
	if ( ! videoUrl ) {
		return;
	}

	var noControls = !! block.getAttribute( 'data-mp4-no-controls' );

	var video = document.createElement( 'video' );
	video.src       = videoUrl;
	video.className = 'poster-video__iframe'; // Reuse same absolute-fill styles.

	// Apply playback options from data attributes.
	if ( block.getAttribute( 'data-mp4-loop' ) )   { video.loop        = true; }
	if ( block.getAttribute( 'data-mp4-muted' ) )  { video.muted       = true; }
	if ( ! noControls )                             { video.controls    = true; }
	if ( block.getAttribute( 'data-mp4-playsinline' ) ) { video.playsInline = true; }

	video.preload = block.getAttribute( 'data-mp4-preload' ) || 'metadata';

	// Swap the inner content.
	var inner = block.querySelector( '.poster-video__inner' );
	if ( inner ) {
		inner.innerHTML = '';
		inner.appendChild( video );
	} else {
		block.innerHTML = '';
		block.appendChild( video );
		inner = block;
	}

	// ── Custom pause/play overlay when native controls are disabled ────────────
	if ( noControls ) {
		var overlay = document.createElement( 'div' );
		overlay.className = 'poster-video__toggle-overlay';
		overlay.setAttribute( 'role', 'button' );
		overlay.setAttribute( 'tabindex', '0' );
		overlay.setAttribute( 'aria-label', 'Pausar vídeo' );

		// Pause icon SVG (two vertical bars inside a dark circle).
		var pauseIcon = document.createElement( 'span' );
		pauseIcon.className = 'poster-video__toggle-icon poster-video__toggle-icon--pause';
		pauseIcon.setAttribute( 'aria-hidden', 'true' );
		pauseIcon.innerHTML = '<svg viewBox="0 0 68 68" xmlns="http://www.w3.org/2000/svg" focusable="false"><circle cx="34" cy="34" r="34" fill="rgba(0,0,0,0.65)"/><rect x="22" y="18" width="10" height="32" fill="#fff"/><rect x="36" y="18" width="10" height="32" fill="#fff"/></svg>';

		// Play icon SVG (triangle inside a dark circle).
		var playIcon = document.createElement( 'span' );
		playIcon.className = 'poster-video__toggle-icon poster-video__toggle-icon--play';
		playIcon.setAttribute( 'aria-hidden', 'true' );
		playIcon.innerHTML = '<svg viewBox="0 0 68 68" xmlns="http://www.w3.org/2000/svg" focusable="false"><circle cx="34" cy="34" r="34" fill="rgba(0,0,0,0.65)"/><polygon points="27,18 54,34 27,50" fill="#fff"/></svg>';

		overlay.appendChild( pauseIcon );
		overlay.appendChild( playIcon );
		inner.appendChild( overlay );

		function togglePlayback() {
			if ( video.paused || video.ended ) {
				video.play().catch( function() {} );
			} else {
				video.pause();
			}
		}

		overlay.addEventListener( 'click', togglePlayback );
		overlay.addEventListener( 'keydown', function( e ) {
			if ( ' ' === e.key || 'Enter' === e.key ) {
				e.preventDefault();
				togglePlayback();
			}
		} );

		// Sync overlay state with actual video events (covers browser-initiated pauses).
		video.addEventListener( 'play', function() {
			overlay.classList.remove( 'is-paused' );
			overlay.setAttribute( 'aria-label', 'Pausar vídeo' );
		} );
		video.addEventListener( 'pause', function() {
			overlay.classList.add( 'is-paused' );
			overlay.setAttribute( 'aria-label', 'Reproducir vídeo' );
		} );
	}

	block.classList.add( 'is-playing' );

	// Start playback; suppress DOMException if browser policy blocks it.
	video.play().catch( function() {} );
}

/**
 * Handle click events via event delegation on the document.
 * Walks the event path upward to find a poster-video block.
 *
 * @param {MouseEvent} event
 */
function handleClick( event ) {
	var target = /** @type {HTMLElement} */ ( event.target );

	// Find the closest play button (if the click was on the button or its children).
	var btn = target.closest( '.poster-video__btn' );

	// Resolve the block wrapper from the button, or directly from the target.
	var block = btn
		? btn.closest( '.wp-block-custom-poster-video' )
		: target.closest( '.wp-block-custom-poster-video' );

	if ( ! block ) {
		return;
	}

	// Ignore if already playing.
	if ( block.classList.contains( 'is-playing' ) ) {
		return;
	}

	// If the click wasn't on the button, ignore clicks on the wrapper
	// itself (e.g. padding area) — only the inner area is interactive.
	if ( ! btn && ! target.closest( '.poster-video__inner' ) ) {
		return;
	}

	if ( 'mp4' === block.getAttribute( 'data-provider' ) ) {
		loadMp4Video( block );
	} else {
		loadIframe( block );
	}
}

/* ── Boot ──────────────────────────────────────────────────────────────────── */

// Attach a single delegated listener on the document.
// This handles all blocks on the page and any loaded later.
document.addEventListener( 'click', handleClick );

// Autoplay MP4: use IntersectionObserver to play when the block enters the viewport.
// The browser requires muted=true for autoplay to work without user interaction.
( function initAutoplayBlocks() {
	var blocks = document.querySelectorAll( '.wp-block-custom-poster-video[data-mp4-autoplay]' );
	if ( ! blocks.length || ! ( 'IntersectionObserver' in window ) ) {
		return;
	}

	blocks.forEach( function( block ) {
		var observer = new IntersectionObserver(
			function( entries ) {
				entries.forEach( function( entry ) {
					if ( entry.isIntersecting && ! block.classList.contains( 'is-playing' ) ) {
						loadMp4Video( block );
						observer.unobserve( block );
					}
				} );
			},
			{ threshold: 0.25 }
		);
		observer.observe( block );
	} );
}() );
